import { useTranslation } from 'react-i18next';
import { IBlogPostListItem } from '@/models/blog-post.model';
import { ROUTES } from '@/models/router.model';
import { EditIcon, EyeIcon, TrashIcon } from '@/icons';
import { Link } from '@/ui';
import { COLORS } from '@/theme';
import { env } from '@/config/env';
import { getListItemSlug } from '@/modules/ContentPages/helpers/blogPostMapper';
import {
  StyledActionsWrap,
  StyledActionButton,
  StyledUrl,
} from '@/modules/Products/components/ProductsTableActions/ProductsTableActionsStyles';

type TContentPagesTableActions = {
  record: IBlogPostListItem;
  onRequestDelete: (record: IBlogPostListItem) => void;
  canManage?: boolean;
};

const ContentPagesTableActions = ({ record, onRequestDelete, canManage }: TContentPagesTableActions) => {
  const { t, i18n } = useTranslation(['common']);
  const editPath = ROUTES.CONTENT_PAGE_DETAILS.replace(':id', record.id);
  const slug = getListItemSlug(record, i18n.language);
  const websiteBase = env.WEBSITE_URL.replace(/\/$/, '');
  const previewUrl = `${websiteBase}/blog/post/preview/${encodeURIComponent(slug)}`;
  const canPreview = Boolean(websiteBase && slug);
  const previewTitle = !websiteBase
    ? t('content_pages_list_action_preview_no_website_url')
    : !slug
    ? t('content_pages_list_action_preview_no_slug')
    : t('content_pages_list_action_preview');

  return (
    <StyledActionsWrap>
      {canManage && (
        <Link path={editPath}>
          <StyledActionButton
            $color={COLORS.PALE_YELLOW}
            aria-label={t('content_pages_list_action_edit')}
            title={t('content_pages_list_action_edit')}
          >
            <EditIcon />
          </StyledActionButton>
        </Link>
      )}
      {canPreview ? (
        <StyledUrl href={previewUrl} target="_blank" rel="noreferrer">
          <StyledActionButton $color={COLORS.GREEN} aria-label={previewTitle} title={previewTitle}>
            <EyeIcon />
          </StyledActionButton>
        </StyledUrl>
      ) : (
        <StyledActionButton $color={COLORS.SOFT_GRAY} disabled aria-label={previewTitle} title={previewTitle}>
          <EyeIcon />
        </StyledActionButton>
      )}
      {canManage && (
        <StyledActionButton
          $color={COLORS.RED}
          aria-label={t('content_pages_list_action_delete')}
          title={t('content_pages_list_action_delete')}
          onClick={() => onRequestDelete(record)}
        >
          <TrashIcon />
        </StyledActionButton>
      )}
    </StyledActionsWrap>
  );
};

export default ContentPagesTableActions;
